import React, { forwardRef } from "react";
import { useFormContext, RegisterOptions } from "react-hook-form";
import cn from "classnames";
import { useMergeRefs } from "storefront/hooks/useMergeRefs";
import { useFormControl } from "../FormControl";
import styles from "./TextArea.module.scss";

interface TextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "name"> {
  rules?: RegisterOptions;
  className?: string;
}

/**
 * TextArea component
 *
 * A multiline text input component that integrates with react-hook-form.
 * Automatically registers with the form and displays validation states.
 */
const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  ({ rules, className, disabled, ...props }, ref) => {
    const {
      register,
      formState: { isSubmitting },
    } = useFormContext();
    const { name, error } = useFormControl();

    const { ref: hookFormRef, ...registerProps } = register(name, rules);

    const mergedRef = useMergeRefs(hookFormRef, ref);

    return (
      <textarea
        id={name}
        ref={mergedRef}
        className={cn(
          styles.textArea,
          { [styles.hasError]: !!error },
          { [styles.disabled]: disabled || isSubmitting },
          className,
        )}
        disabled={disabled || isSubmitting}
        {...registerProps}
        {...props}
      />
    );
  },
);

TextArea.displayName = "TextArea";

export default TextArea;
